import React, { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { usePayments } from '@/hooks/use-payments';
import { PaymentRecord } from '@/types/payment';
import { Upload, Download, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

interface PaymentsDataControlsProps {
  payments: PaymentRecord[];
  onOpenBulkCreate: () => void;
}

export function PaymentsDataControls({ payments, onOpenBulkCreate }: PaymentsDataControlsProps) {
  const { importPayments, autoUpdateOverdues } = usePayments();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const download = (data: string, filename: string, type: string) => {
    const blob = new Blob([data], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportJSON = () => {
    const json = JSON.stringify(payments, null, 2);
    download(json, `payments-${new Date().toISOString()}.json`, 'application/json');
  };

  const toCSV = (rows: PaymentRecord[]) => {
    const headers = [
      'id','memberId','memberName','amount','currency','paymentDate','dueDate','status','paymentMethod','description','adminId','createdAt','updatedAt'
    ];
    const escape = (val: any) => {
      const s = val === null || val === undefined ? '' : String(val);
      if (/[",\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
      return s;
    };
    const lines = [headers.join(',')];
    for (const r of rows) {
      const values = headers.map(h => escape((r as any)[h]));
      lines.push(values.join(','));
    }
    return lines.join('\n');
  };

  const exportCSV = () => {
    const csv = toCSV(payments);
    download(csv, `payments-${new Date().toISOString()}.csv`, 'text/csv');
  };

  const parseCSV = (text: string): Partial<PaymentRecord>[] => {
    const lines = text.split(/\r?\n/).filter(Boolean);
    if (lines.length <= 1) return [];
    const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
    const rows: Partial<PaymentRecord>[] = [];
    for (let i = 1; i < lines.length; i++) {
      const row: any = {};
      // naive CSV split handling quotes
      const cells: string[] = [];
      let cur = '';
      let inQuotes = false;
      for (const ch of lines[i]) {
        if (ch === '"') {
          inQuotes = !inQuotes;
          continue;
        }
        if (ch === ',' && !inQuotes) {
          cells.push(cur);
          cur = '';
        } else {
          cur += ch;
        }
      }
      cells.push(cur);
      headers.forEach((h, idx) => {
        const raw = (cells[idx] || '').trim();
        row[h] = raw;
      });
      rows.push(row);
    }
    return rows;
  };

  const onImportClick = () => fileInputRef.current?.click();

  const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      let parsed: Partial<PaymentRecord>[] = [];
      if (file.name.endsWith('.json')) {
        parsed = JSON.parse(text);
      } else if (file.name.endsWith('.csv')) {
        parsed = parseCSV(text);
      } else {
        toast.error('Unsupported file format. Use .json or .csv');
        return;
      }
      const imported = await importPayments(parsed);
      toast.success(`Imported ${imported.length} payments`);
      e.target.value = '';
    } catch (err) {
      console.error(err);
      toast.error('Failed to import file');
    }
  };

  const handleAutoOverdue = async () => {
    const changed = await autoUpdateOverdues();
    if (changed) toast.success('Updated overdue payments');
    else toast.info('No payments to update');
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-2">
        <Button onClick={onOpenBulkCreate} size="sm">
          <Upload className="h-4 w-4 mr-2" />
          Bulk Create Dues
        </Button>
        <Button variant="outline" onClick={handleAutoOverdue} size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Auto-mark Overdue
        </Button>
        <Button variant="outline" onClick={exportCSV} size="sm">
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
        <Button variant="outline" onClick={exportJSON} size="sm">
          <Download className="h-4 w-4 mr-2" />
          Export JSON
        </Button>
        <Button variant="outline" onClick={onImportClick} size="sm">
          <Upload className="h-4 w-4 mr-2" />
          Import CSV/JSON
        </Button>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept=".json,.csv"
        className="hidden"
        onChange={handleFileChange}
      />
    </div>
  );
}
